import { useState, useContext, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Edit, Download, Upload } from "lucide-react";
import { ProjectContext } from "./ProjectStructureTab";
import ChartLoader from "./time-schedule/ChartLoader";
import GanttChart from "./time-schedule/GanttChart";
import TaskEditDialog from "./time-schedule/TaskEditDialog";
import ProjectInfoHeader from "./shared/ProjectInfoHeader";
import { ProjectDialogs } from "./dialogs/ProjectDialogs";
import { useProjects } from "./hooks/useProjects";
import { useUserRole } from "./hooks/useUserRole";
import { useScheduleEditProjectAccess } from "./hooks/useScheduleEditProjectAccess";
import { useScheduleExportAccess } from "./hooks/useScheduleExportAccess";
import { supabase } from "@/integrations/supabase/client";
import { convertTasksToJson, ProjectTask, ProjectTemplate, JiraImportData } from "./types";
import { ProjectSubtask } from "./task-management/types";
import { toast } from "@/components/ui/use-toast";
import { 
  generateJiraProjectKey, 
  getJiraResourceMappings
} from "./jira/jiraExportUtils";
import { 
  calculateProgressFromJiraStatus, 
  calculateTaskProgressFromSubtasks, 
  validateJiraImportData 
} from "./jira/jiraImportUtils";
import { transformToJiraCompatible } from "./jira/jiraTransformUtils";
import { transformToSimplifiedJiraFormat } from "./jira/jiraSimplifiedTransform";
import { JiraCompatibleImportData } from "./jira/jiraCompatibleTypes";
import JiraCompanyNameDialog from "./jira/JiraCompanyNameDialog";
import { transformToJiraAccountFormat } from "./jira/jiraAccountUtils";
import { transformToJiraOfficialFormat } from "./jira/jiraOfficialTransform";
import { exportToPrimaveraXLSX } from "./utils/primaveraExport";
import { exportToMSProjectXML } from "./utils/msProjectExport";
import { User } from "@supabase/supabase-js";

interface TimeScheduleTabProps {
  user: User | null;
}

const TimeScheduleTab = ({ user }: TimeScheduleTabProps) => {
  const { selectedProject, loading } = useContext(ProjectContext);
  const [chartLoaded, setChartLoaded] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [isCompanyNameDialogOpen, setIsCompanyNameDialogOpen] = useState(false);
  const [resourceNames, setResourceNames] = useState<string[]>([]);
  const [assignmentsForExport, setAssignmentsForExport] = useState<any[]>([]);
  const [subtasks, setSubtasks] = useState<ProjectSubtask[]>([]);
  const [isExportingPrimavera, setIsExportingPrimavera] = useState(false);
  const [taskNameToUuid, setTaskNameToUuid] = useState<Record<string, string>>({});
  // Map of project_tasks.id -> fresh completion_percentage from the DB.
  // Refreshed on every Schedule Management visit / subtask change so the
  // Gantt always reflects the latest progress without any client caching.
  const [taskProgressById, setTaskProgressById] = useState<Record<string, number>>({});
  const [taskProgressByName, setTaskProgressByName] = useState<Record<string, number>>({});
  // Root ref used to observe the enclosing Radix TabsContent data-state.
  // Whenever this tab becomes active we bump tabActivationKey to force a
  // fresh subtask + task-progress reload — no cached progress is reused.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [tabActivationKey, setTabActivationKey] = useState(0);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const panel = el.closest('[data-state]') as HTMLElement | null;
    if (!panel) return;
    if (panel.getAttribute('data-state') === 'active') {
      setTabActivationKey((k) => k + 1);
    }
    const observer = new MutationObserver(() => {
      if (panel.getAttribute('data-state') === 'active') {
        setTabActivationKey((k) => k + 1);
      }
    });
    observer.observe(panel, { attributes: true, attributeFilter: ['data-state'] });
    return () => observer.disconnect();
  }, []);
  
  const {
    isEditProjectDialogOpen,
    setIsEditProjectDialogOpen,
    newProject,
    setNewProject,
    excludeWeekends,
    setExcludeWeekends,
    handleTaskDurationChange
  } = useProjects(templates);

  // Role-based access control
  const { role } = useUserRole(user);
  const isRestrictedUser = role === "Team member" || role === "Team Lead";
  const { canEditProject: canEditScheduleProject } = useScheduleEditProjectAccess(user);
  const { canExportMSProject, canExportPrimavera } = useScheduleExportAccess(user);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setTemplates([]);
      } catch (error) {
        console.error('Error loading templates:', error);
        setTemplates([]);
      }
    };

    loadTemplates();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      setChartLoaded(false);
      setTimeout(() => setChartLoaded(true), 100);
    }
  }, [selectedProject]);

  // Fetch resource assignments for the selected project to gate Export to XLSX button
  useEffect(() => {
    const fetchAssignments = async () => {
      if (!selectedProject?.id) {
        setAssignmentsForExport([]);
        return;
      }
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (!authUser) {
          setAssignmentsForExport([]);
          return;
        }
        const { data, error } = await supabase
          .from('resource_assignment')
          .select('*')
          .eq('project_id', selectedProject.id)
          .eq('user_id', authUser.id);
        if (error) throw error;
        const rows = data || [];
        setAssignmentsForExport(rows);
      } catch (err) {
        console.error('Error fetching resource assignments for export gating:', err);
        setAssignmentsForExport([]);
      }
    };
    fetchAssignments();
  }, [selectedProject?.id, refreshTrigger]);

  // Fetch subtasks for the selected project + name->UUID map, and subscribe to realtime changes
  useEffect(() => {
    if (!selectedProject?.id) {
      setSubtasks([]);
      setTaskNameToUuid({});
      return;
    }

    let cancelled = false;
    let requestSeq = 0;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const loadSubtasks = async () => {
      const currentRequest = ++requestSeq;
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (!authUser) return;

        // Determine visibility scope for the Gantt:
        // - Director: all subtasks (no assignee filter)
        // - Everyone else (Manager, Team Lead, Team Member, etc.):
        //   strictly OWN assigned subtasks (filter by id OR email match)
        const scope: 'all' | 'own' = role === 'Director' ? 'all' : 'own';

        // Single source of truth: edge function returns subtasks plus
        // task_name on each row, so we no longer need duplicate client
        // project_tasks / project_subtasks queries (reduces Disk IO).
        let visibleSubs: ProjectSubtask[] = [];
        const { data: fnData, error: fnError } = await supabase.functions.invoke(
          'get-project-task-subtasks',
          { body: { project_id: selectedProject.id, scope } }
        );
        if (fnError) {
          console.error('Error loading subtasks via edge function:', fnError);
        } else {
          visibleSubs = ((fnData as any)?.data || []) as ProjectSubtask[];
        }

        if (cancelled || currentRequest !== requestSeq) return;

        const freshTasks = ((fnData as any)?.tasks || []) as Array<{ id: string; task_id?: string | null; name: string | null; completion_percentage: number | null }>;

        // Build name->UUID map directly from edge function output.
        const nameMap: Record<string, string> = {};
        for (const task of freshTasks) {
          if (task?.name && task?.id) nameMap[task.name] = task.id;
        }
        for (const s of visibleSubs) {
          if (s.task_name && s.task_id && !nameMap[s.task_name]) nameMap[s.task_name] = s.task_id;
        }

        // Fresh task progress (completion_percentage) returned by the edge
        // function. Always rebuilt on every load — no caching.
        const progressById: Record<string, number> = {};
        const progressByName: Record<string, number> = {};
        for (const t of freshTasks) {
          // Only record numeric percentages — skip null/undefined so we never
          // overwrite cached progress with 0 on subsequent renders.
          if (typeof t.completion_percentage !== 'number' || !isFinite(t.completion_percentage)) continue;
          if (t.id) progressById[t.id] = t.completion_percentage;
          if (t.task_id) progressById[t.task_id] = t.completion_percentage;
          if (t.name) progressByName[t.name] = t.completion_percentage;
        }

        if (cancelled || currentRequest !== requestSeq) return;
        setTaskNameToUuid(nameMap);
        setTaskProgressById(progressById);
        setTaskProgressByName(progressByName);
        setSubtasks(visibleSubs);
      } catch (err) {
        console.error('Error loading subtasks for Gantt:', err);
        if (!cancelled && currentRequest === requestSeq) {
          setSubtasks([]);
        }
      }
    };


    loadSubtasks();

    const channel = supabase
      .channel(`gantt-subtasks-${selectedProject.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'project_subtasks', filter: `project_id=eq.${selectedProject.id}` },
        () => {
          // Debounce bursts of realtime events (e.g. bulk subtask updates)
          // to avoid hammering the edge function and DB on every row change.
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => loadSubtasks(), 1000);
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'project_tasks', filter: `project_id=eq.${selectedProject.id}` },
        () => {
          // Trigger-driven completion_percentage updates fire here; reload so
          // the Gantt's parent-task progress reflects subtask changes made by
          // anyone (including users outside the caller's reporting line).
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => loadSubtasks(), 400);
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [selectedProject?.id, refreshTrigger, role, tabActivationKey]);

  useEffect(() => {
    if (!selectedProject?.id) return;

    const handleProjectTasksUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{
        projectId?: string;
        tasks?: any[];
        updatedTaskId?: string;
        updatedTaskName?: string;
        newProgress?: number;
      }>).detail;

      if (!detail || detail.projectId !== selectedProject.id) return;

      if (Array.isArray(detail.tasks)) {
        selectedProject.tasks = detail.tasks;
      }

      if (typeof detail.newProgress === 'number' && isFinite(detail.newProgress)) {
        const nextProgress = Math.round(detail.newProgress);

        if (detail.updatedTaskId) {
          setTaskProgressById((prev) => ({ ...prev, [detail.updatedTaskId!]: nextProgress }));
        }

        if (detail.updatedTaskName) {
          setTaskProgressByName((prev) => ({ ...prev, [detail.updatedTaskName!]: nextProgress }));
        }
      }
    };

    window.addEventListener('projectTasksUpdated', handleProjectTasksUpdated as EventListener);
    return () => {
      window.removeEventListener('projectTasksUpdated', handleProjectTasksUpdated as EventListener);
    };
  }, [selectedProject]);





  const exportToJiraJSON = async () => {
    if (!selectedProject || !selectedProject.tasks) {
      toast({
        title: "Export Error",
        description: "No project data available for export",
        variant: "destructive"
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data: resourceAssignments } = await supabase
        .from('resource_assignment')
        .select('*')
        .eq('project_id', selectedProject.id);

      // Use default company name if not set
      const companyName = localStorage.getItem('jira-company-name') || 'company';
      
      // Generate project key
      const projectKey = generateJiraProjectKey(selectedProject.name);

      // Use the new official Jira format transformer
      const jiraOfficialData = transformToJiraOfficialFormat(
        selectedProject.name,
        projectKey,
        selectedProject.description || '',
        selectedProject.projectLead || '',
        selectedProject.tasks,
        resourceAssignments || [],
        companyName
      );

      const exportData = {
        ...jiraOfficialData,
        metadata: {
          exportDate: new Date().toISOString(),
          exportedBy: user?.email || 'Unknown',
          originalProjectId: selectedProject.id,
          version: '5.0-jira-official-format'
        }
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
        type: 'application/json' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedProject.name}_Jira_Official_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Export Successful",
        description: `Jira-compliant project data exported (${selectedProject.tasks.length} tasks, ${jiraOfficialData.users.length} users)`
      });

    } catch (error) {
      console.error('Error exporting to Jira:', error);
      toast({
        title: "Export Error",
        description: "Failed to export project data",
        variant: "destructive"
      });
    }
  };

  const performJiraExport = async (
    resourceAssignments: any[], 
    jiraResourceMappings?: Record<string, string>, 
    companyName?: string
  ) => {
    if (!selectedProject) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const projectKey = generateJiraProjectKey(selectedProject.name);

      // Use the new account-based format with actual emails
      const jiraAccountData = transformToJiraAccountFormat(
        selectedProject.name,
        projectKey,
        selectedProject.description || '',
        selectedProject.projectLead || '',
        selectedProject.tasks,
        resourceAssignments
      );

      const exportData = {
        ...jiraAccountData,
        metadata: {
          exportDate: new Date().toISOString(),
          exportedBy: user?.email || 'Unknown',
          originalProjectId: selectedProject.id,
          version: '4.0-jira-account-format'
        }
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
        type: 'application/json' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedProject.name}_Jira_Account_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Export Successful",
        description: `Jira account-based project data exported (${selectedProject.tasks.length} tasks, ${jiraAccountData.users.length} users)`
      });

    } catch (error) {
      console.error('Error performing Jira export:', error);
      toast({
        title: "Export Error",
        description: "Failed to export project data",
        variant: "destructive"
      });
    }
  };

  const importFromJiraJSON = async (file: File) => {
    try {
      const text = await file.text();
      const importData = JSON.parse(text);
      
      let processedData: JiraImportData;
      
      if (importData.projects && Array.isArray(importData.projects)) {
        const jiraProject = importData.projects[0];
        if (!jiraProject) {
          throw new Error('No project data found in import file');
        }
        
        processedData = {
          project: {
            key: jiraProject.key,
            name: jiraProject.name
          },
          issues: jiraProject.issues.map((issue: any) => ({
            key: issue.externalId || issue.key || issue.summary,
            summary: issue.summary,
            status: {
              name: issue.status?.name || 'To Do',
              category: issue.status?.category || 'new'
            },
            progress: {
              percent: issue.customFields?.originalProgress || 0
            },
            subtasks: issue.subtasks || []
          })),
          metadata: {
            exportDate: importData.metadata?.exportDate || new Date().toISOString(),
            sourceSystem: 'jira-compatible'
          }
        };
      } else {
        if (!validateJiraImportData(importData)) {
          throw new Error('Invalid Jira import data format');
        }
        processedData = importData;
      }
      
      if (!selectedProject) {
        toast({
          title: "Import Error",
          description: "No project selected",
          variant: "destructive"
        });
        return;
      }
      
      const updatedTasks = selectedProject.tasks?.map(task => {
        const jiraIssue = processedData.issues.find(issue => 
          issue.summary === task.name || 
          issue.key === task.jiraIssueKey ||
          issue.key === task.taskId
        );
        
        if (jiraIssue) {
          const calculatedProgress = jiraIssue.progress?.percent || 
            calculateProgressFromJiraStatus(jiraIssue.status.name);
          
          const finalProgress = calculateTaskProgressFromSubtasks(
            calculatedProgress,
            jiraIssue.subtasks
          );
          
          return {
            ...task,
            progress: finalProgress,
            jiraIssueKey: jiraIssue.key,
            jiraStatus: jiraIssue.status.name,
            jiraSubTasks: jiraIssue.subtasks || [],
            lastJiraSync: new Date().toISOString()
          };
        }
        return task;
      });
      
      const { error } = await supabase
        .from('projects')
        .update({
          project_tasks: convertTasksToJson(updatedTasks || [])
        })
        .eq('id', selectedProject.id);
      
      if (error) throw error;
      
      if (selectedProject && updatedTasks) {
        selectedProject.tasks = updatedTasks;
      }
      
      setChartLoaded(false);
      setRefreshTrigger(prev => prev + 1);
      setTimeout(() => setChartLoaded(true), 100);
      
      toast({
        title: "Import Successful",
        description: `Progress updated from Jira for ${processedData.issues.length} tasks`
      });
      
    } catch (error) {
      console.error('Error importing Jira data:', error);
      toast({
        title: "Import Error",
        description: "Failed to import Jira data: " + (error as Error).message,
        variant: "destructive"
      });
    }
  };

  const handleJiraImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/json') {
      importFromJiraJSON(file);
    } else {
      toast({
        title: "Invalid File",
        description: "Please select a valid JSON file",
        variant: "destructive"
      });
    }
    event.target.value = '';
  };

  const handleEditProject = () => {
    if (selectedProject) {
      setNewProject({
        name: selectedProject.name,
        description: selectedProject.description,
        projectLead: selectedProject.projectLead,
        startDate: selectedProject.startDate,
        endDate: selectedProject.endDate,
        type: selectedProject.template || "",
        duration: selectedProject.duration,
        tasks: selectedProject.tasks || []
      });
      setExcludeWeekends(selectedProject.excludeWeekends ?? true);
      setIsEditProjectDialogOpen(true);
    }
  };

  const confirmEditProject = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedProject || !newProject.name.trim()) {
      return;
    }
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return;
      }

      const projectEndDate = newProject.tasks.length > 0 && newProject.tasks[newProject.tasks.length - 1].endDate
        ? newProject.tasks[newProject.tasks.length - 1].endDate
        : newProject.endDate;
      
      const { error } = await supabase
        .from('projects')
        .update({
          project_name: newProject.name,
          description: newProject.description,
          project_lead: newProject.projectLead,
          start_date: newProject.startDate,
          end_date: projectEndDate,
          duration: newProject.tasks.reduce((sum, task) => sum + task.duration, 0),
          exclude_weekends: excludeWeekends,
          project_tasks: convertTasksToJson(newProject.tasks)
        })
        .eq('id', selectedProject.id);
      
      if (!error) {
        if (selectedProject) {
          selectedProject.name = newProject.name;
          selectedProject.description = newProject.description;
          selectedProject.projectLead = newProject.projectLead;
          selectedProject.startDate = newProject.startDate;
          selectedProject.endDate = projectEndDate;
          selectedProject.duration = newProject.tasks.reduce((sum, task) => sum + task.duration, 0);
          selectedProject.excludeWeekends = excludeWeekends;
          selectedProject.tasks = newProject.tasks;
        }

        setChartLoaded(false);
        setRefreshTrigger(prev => prev + 1);
        setIsEditProjectDialogOpen(false);
        
        setTimeout(() => {
          setChartLoaded(true);
        }, 100);
      }
      
    } catch (error) {
      console.error('Error updating project:', error);
    }
  };

  const handleTaskSelect = (task: any, index: number) => {
    setSelectedTask({
      ...task,
      index
    });
    setIsTaskDialogOpen(true);
  };

  const handleTaskUpdated = async () => {
    setChartLoaded(false);
    setRefreshTrigger(prev => prev + 1);
    
    if (selectedProject && selectedProject.id) {
      try {
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .eq('id', selectedProject.id)
          .single();
          
        if (error) throw error;
        
        if (data) {
          if (data.project_tasks) {
            let parsedTasks: ProjectTask[] = [];
            try {
              if (typeof data.project_tasks === 'string') {
                parsedTasks = JSON.parse(data.project_tasks) as ProjectTask[];
              } else if (Array.isArray(data.project_tasks)) {
                const jsonArray = data.project_tasks as any[];
                parsedTasks = jsonArray
                  .filter(item => 
                    typeof item === 'object' && 
                    item !== null && 
                    'name' in item && 
                    'duration' in item
                  )
                  .map(item => ({
                    name: item.name,
                    duration: item.duration,
                    id: item.id,
                    progress: item.progress || 0,
                    isCritical: item.isCritical || false,
                    isMilestone: item.isMilestone || false,
                    phase: item.phase || "Main Tasks",
                    startDate: item.startDate,
                    endDate: item.endDate,
                    dependencies: item.dependencies || ""
                  }));
              }
              
              selectedProject.tasks = parsedTasks;
              // Sync project-level dates from freshly fetched data
              if (data.start_date) {
                selectedProject.startDate = data.start_date;
              }
              if (data.end_date) {
                selectedProject.endDate = data.end_date;
              }
            } catch (parseError) {
              console.error("Error parsing task data:", parseError);
              selectedProject.tasks = [];
            }
          } else {
            selectedProject.tasks = [];
          }
        }
      } catch (error) {
        console.error("Error refreshing project data:", error);
      }
    }
    
    setTimeout(() => {
      console.log('Re-enabling chart after task update');
      setChartLoaded(true);
    }, 50);
  };

  return (
    <div ref={rootRef} className="grid grid-cols-12 gap-2 my-0 py-0">
      {selectedProject && (
        <ProjectInfoHeader selectedProject={selectedProject} />
      )}

      <div className="col-span-12">
        <Card className="bg-secondary-light/5 border-2 border-primary text-white">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between">
              <div>
                <span className="text-white">Project Timeline</span>
                <div className="text-gray-500 text-[14px] py-2">(To change the project task sequence, click the Edit Project button. To edit a project task, click the task on the Gantt Chart.)
                </div>
              </div>
              {selectedProject && (
                <div className="flex gap-2">
                  <Button
                    onClick={() => exportToMSProjectXML(selectedProject, assignmentsForExport, subtasks as any, taskNameToUuid)}
                    disabled={!canExportMSProject}
                    className={`${
                      !canExportMSProject
                        ? "bg-gray-600 text-gray-400 cursor-not-allowed hover:bg-gray-600"
                        : "bg-primary text-black hover:bg-primary-400"
                    }`}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export to MS Project
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!selectedProject?.id) return;
                      setIsExportingPrimavera(true);
                      try {
                        // Lazy fetch: only pull the full unfiltered subtask
                        // set when the user actually clicks Export — avoids
                        // a project-wide SELECT on every Gantt load /
                        // realtime tick.
                        const { data: exportData, error: exportError } = await supabase
                          .from('project_subtasks')
                          .select('*')
                          .eq('project_id', selectedProject.id);
                        if (exportError) throw exportError;
                        const rows = (exportData || []) as ProjectSubtask[];
                        const nameMap = { ...taskNameToUuid };
                        const missingIds = Array.from(
                          new Set(
                            rows
                              .map((s) => s.task_id)
                              .filter((id): id is string => !!id && !Object.values(nameMap).includes(id))
                          )
                        );
                        if (missingIds.length > 0) {
                          const { data: namedTasks } = await supabase
                            .from('project_tasks')
                            .select('id, name')
                            .in('id', missingIds);
                          (namedTasks || []).forEach((t: any) => {
                            if (t?.id && t?.name) nameMap[t.name] = t.id;
                          });
                        }
                        exportToPrimaveraXLSX(selectedProject, assignmentsForExport, rows as any, nameMap);
                      } catch (err) {
                        console.error('Primavera export failed:', err);
                        toast({
                          title: 'Export Error',
                          description: 'Failed to load subtasks for export',
                          variant: 'destructive',
                        });
                      } finally {
                        setIsExportingPrimavera(false);
                      }
                    }}
                    disabled={!canExportPrimavera || isExportingPrimavera}
                    className={`${
                      !canExportPrimavera
                        ? "bg-gray-600 text-gray-400 cursor-not-allowed hover:bg-gray-600"
                        : "bg-primary text-black hover:bg-primary-400"
                    }`}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export to Primavera
                  </Button>

                  <Button
                    onClick={handleEditProject}
                    disabled={!canEditScheduleProject}
                    className={`${
                      !canEditScheduleProject
                        ? "bg-gray-600 text-gray-400 cursor-not-allowed hover:bg-gray-600"
                        : "bg-primary text-black hover:bg-primary-400"
                    }`}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Project
                  </Button>
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ChartLoader onChartLoaded={setChartLoaded} />
            
            {loading ? (
              <div className="flex justify-center items-center h-[450px]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : selectedProject ? (
              <div className="border border-primary rounded-lg p-4 bg-secondary-light/10">
                <GanttChart 
                  selectedProject={{
                    ...selectedProject,
                    tasks: (selectedProject.tasks || []).map((t: any) => {
                      // Prefer fresh DB completion_percentage by project_tasks.id
                      // only. Never map project_subtasks.task_id to the legacy
                      // project_tasks.task_id text code for Gantt progress.
                      // over the cached project_tasks JSON progress so the Gantt
                      // always reflects the latest subtask-driven progress.
                      const taskUuid = t?.id || (t?.name ? taskNameToUuid[t.name] : undefined);
                      const byId = taskUuid ? taskProgressById[taskUuid] : undefined;
                      const byName = t?.name ? taskProgressByName[t.name] : undefined;
                      const freshProgress = typeof byId === 'number'
                        ? byId
                        : (typeof byName === 'number' ? byName : undefined);
                      return typeof freshProgress === 'number'
                        ? { ...t, progress: Math.round(freshProgress) }
                        : t;
                    }),
                  }}
                  chartLoaded={chartLoaded}
                  onTaskSelect={handleTaskSelect}
                  isRestrictedUser={isRestrictedUser}
                  subtasks={subtasks}
                  taskNameToUuid={taskNameToUuid}
                  defaultExpandSubtasks={role !== "Director"}
                  key={`chart-${refreshTrigger}`}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[450px] text-center">
                <h3 className="text-xl font-medium mb-4">No Project Selected</h3>
                <p className="text-gray-500 mb-8 max-w-md">
                  Please select a project from the Project Structure tab to view its timeline.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      <TaskEditDialog
        isOpen={isTaskDialogOpen}
        onOpenChange={setIsTaskDialogOpen}
        selectedTask={selectedTask}
        setSelectedTask={setSelectedTask}
        selectedProject={selectedProject}
        isAddingNewTask={false}
        onTaskUpdated={handleTaskUpdated}
        isRestrictedUser={isRestrictedUser}
      />

      <ProjectDialogs
        isNewProjectDialogOpen={false}
        setIsNewProjectDialogOpen={() => {}}
        isEditProjectDialogOpen={isEditProjectDialogOpen}
        setIsEditProjectDialogOpen={setIsEditProjectDialogOpen}
        isDeleteProjectDialogOpen={false}
        setIsDeleteProjectDialogOpen={() => {}}
        isImportAlertOpen={false}
        setIsImportAlertOpen={() => {}}
        isImportDialogOpen={false}
        setIsImportDialogOpen={() => {}}
        selectedProject={selectedProject}
        newProject={newProject}
        setNewProject={setNewProject}
        handleCreateProject={() => {}}
        confirmEditProject={confirmEditProject}
        confirmDeleteProject={() => {}}
        handleImportTemplate={() => {}}
        templates={templates}
        handleTaskDurationChange={handleTaskDurationChange}
        excludeWeekends={excludeWeekends}
        setExcludeWeekends={setExcludeWeekends}
      />

      <JiraCompanyNameDialog
        isOpen={isCompanyNameDialogOpen}
        onOpenChange={setIsCompanyNameDialogOpen}
        onCompanyNameSubmit={() => {}}
      />
    </div>
  );
};

export default TimeScheduleTab;
